import React from "react"
import styled, { css } from "styled-components"
import { Link } from "gatsby"

import Layout from "../layouts/layout"
import Seo from "../components/seo"
import ReadLink from "../components/read-link"
import { UiText, Heading } from "../components"
import pubPaper from "../images/projects/pub_paper.svg"

const ResearchPage = () => (
  <Layout>
    <Seo title="Research" />
    <div
      css={css`
        position: relative;
        max-width: 960px;
        margin: auto;
        padding: 40px 0px;
      `}
    >
      <Heading level={1} as="h1" variant="h1">
        Research
      </Heading>

      <UiText variant="content">
        My work centers on computer architecture and hardware acceleration:
        precision-flexible datapaths, systolic-array dataflow, and performance
        modeling for edge AI inference. The thread running through it is making
        domain-specific accelerators that stay efficient at the small problem
        sizes real edge workloads actually run.
      </UiText>

      <Heading level={2} as="h2" variant="h2">
        Current work
      </Heading>
      <UiText variant="content">
        My MS thesis designs a multi-precision GEMM accelerator on a Cyclone V SoC
        and measures the area, timing, and energy cost of precision flexibility
        against fixed-precision baselines, paired with a precision-aware roofline
        analysis. A companion study quantifies where GPUs lose efficiency at edge
        GEMM dimensions, the warp-scheduling, register-file, and
        memory-controller overheads, using GPGPU-Sim and an RTX 4090. The
        accelerator runs end to end on real silicon, from RTL through a full
        Linux bring-up.{" "}
        <Link to="/projects/gemm-accelerator-cyclonev">
          Read the accelerator case study
        </Link>
        .
      </UiText>

      <Heading level={2} as="h2" variant="h2">
        Interests
      </Heading>
      <UiText variant="content">
        Domain-specific architecture, reduced- and mixed-precision arithmetic,
        dataflow and memory hierarchy for accelerators, hardware/software
        co-design, and performance and energy modeling. I work at the
        intersection of hardware engineering and computer-architecture research,
        and I am open to hardware and accelerator engineering roles, industry
        research positions, and PhD programs, anywhere the work is building
        efficient domain-specific hardware.
      </UiText>

      <Heading level={2} as="h2" variant="h2">
        Publications
      </Heading>
      <Row>
        <a
          href="https://ieeexplore.ieee.org/document/11366479"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Thumb>
            <ThumbImg src={pubPaper} alt="CacheFormer paper" />
          </Thumb>
        </a>
        <Content>
          <RowTitle>
            <a
              href="https://ieeexplore.ieee.org/document/11366479"
              target="_blank"
              rel="noopener noreferrer"
            >
              CacheFormer
            </a>{" "}
            (IEEE CCNC 2026)
          </RowTitle>
          <UiText variant="content" as="p" margin="0px">
            An Autoformer-based predictive caching system using sparse
            auto-correlation to reduce attention cost to O(W log W).
          </UiText>
          <ReadLink
            as="a"
            href="https://ieeexplore.ieee.org/document/11366479"
            target="_blank"
            rel="noopener noreferrer"
          >
            view on IEEE Xplore &rarr;
          </ReadLink>
        </Content>
      </Row>
    </div>
  </Layout>
)

const Row = styled.article`
  border-bottom: 1px solid #ddd;
  display: flex;
  margin-top: 1rem;
  padding-bottom: 1rem;
  align-items: flex-start;
  max-width: 960px;
`
const Thumb = styled.div`
  margin: 0 1rem 0 0;
  width: 100px;
  height: 100px;
  flex: 0 0 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`
const ThumbImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  margin: 0;
`
const Content = styled.div`
  flex: 1;
`
const RowTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 0.25rem;
  color: var(--f-color-brandPrimary);
  a {
    color: var(--f-color-brandPrimary);
  }
`

export default ResearchPage
